import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { usersAPI } from '../services/api';
import { User, Mail, Phone, MapPin, Building, CreditCard, Save, Loader2 } from 'lucide-react';

const Profile = () => {
  const { isDarkMode } = useTheme();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    usersAPI.getMe()
      .then((res) => {
        setProfile(res.data);
        setForm(res.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await usersAPI.updateMe(form);
      setProfile(res.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {}
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const update = (key, value) => setForm({ ...form, [key]: value });

  return (
    <div className="max-w-3xl mx-auto py-6 px-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Mi Perfil</h1>
            <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              STTORA ID: {profile?.sttora_id} · {profile?.role === 'gestor' ? 'Gestor Independiente' : profile?.role === 'admin' ? 'Administrador' : 'Cliente Premium'}
            </p>
          </div>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 bg-indigo-600 text-white px-6 py-3 rounded-xl hover:bg-indigo-700 disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saved ? 'Guardado!' : 'Guardar'}
          </button>
        </div>

        {saved && (
          <div className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 p-4 rounded-xl text-sm">
            Perfil actualizado exitosamente
          </div>
        )}

        <div className={`${isDarkMode ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-sm rounded-3xl p-6 border shadow-sm space-y-6`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm text-gray-500 flex items-center gap-2"><User className="w-4 h-4" /> Nombre</label>
              <input value={form.first_name || ''} onChange={(e) => update('first_name', e.target.value)}
                className="w-full p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600 mt-1" />
            </div>
            <div>
              <label className="text-sm text-gray-500 flex items-center gap-2"><User className="w-4 h-4" /> Apellido</label>
              <input value={form.last_name || ''} onChange={(e) => update('last_name', e.target.value)}
                className="w-full p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600 mt-1" />
            </div>
            <div>
              <label className="text-sm text-gray-500 flex items-center gap-2"><Mail className="w-4 h-4" /> Email</label>
              <input value={form.email || ''} disabled className="w-full p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600 mt-1 opacity-60" />
            </div>
            <div>
              <label className="text-sm text-gray-500 flex items-center gap-2"><Phone className="w-4 h-4" /> Teléfono</label>
              <input value={form.phone || ''} onChange={(e) => update('phone', e.target.value)}
                className="w-full p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600 mt-1" />
            </div>
            <div>
              <label className="text-sm text-gray-500 flex items-center gap-2"><MapPin className="w-4 h-4" /> País</label>
              <input value={form.country || ''} onChange={(e) => update('country', e.target.value)}
                className="w-full p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600 mt-1" />
            </div>
          </div>

          <hr className="dark:border-gray-700" />

          <div>
            <h3 className="font-semibold mb-4 flex items-center gap-2"><MapPin className="w-4 h-4" /> Dirección</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input placeholder="Calle" value={form.street_name || ''} onChange={(e) => update('street_name', e.target.value)}
                className="p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600" />
              <input placeholder="Número" value={form.street_number || ''} onChange={(e) => update('street_number', e.target.value)}
                className="p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600" />
              <input placeholder="Ciudad" value={form.city || ''} onChange={(e) => update('city', e.target.value)}
                className="p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600" />
              <input placeholder="Estado" value={form.state || ''} onChange={(e) => update('state', e.target.value)}
                className="p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600" />
              <input placeholder="Código Postal" value={form.zip_code || ''} onChange={(e) => update('zip_code', e.target.value)}
                className="p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600" />
            </div>
          </div>

          {profile?.role === 'gestor' && (
            <>
              <hr className="dark:border-gray-700" />
              <div>
                <h3 className="font-semibold mb-4 flex items-center gap-2"><Building className="w-4 h-4" /> Información Bancaria</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <input placeholder="RUC" value={form.ruc || ''} onChange={(e) => update('ruc', e.target.value)}
                    className="p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600" />
                  <input placeholder="Banco" value={form.bank_name || ''} onChange={(e) => update('bank_name', e.target.value)}
                    className="p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600" />
                  <input placeholder="IBAN/Cuenta" value={form.bank_account || ''} onChange={(e) => update('bank_account', e.target.value)}
                    className="p-3 border rounded-xl dark:bg-gray-700 dark:border-gray-600" />
                </div>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default Profile;
